import * as React from "react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { ExternalLink, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useCarriers } from "@/hooks/useSamples";
import { formatDate, formatStatusLabel } from "@/lib/format";
import { useAppLocale } from "@/lib/i18n";
import { m } from "@/paraglide/messages.js";

interface SampleEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sample: any;
}

function getStatusOptions() {
  return [
    { value: "pending", label: m.status_requested() },
    { value: "approved", label: m.status_approved() },
    { value: "shipped", label: m.status_shipped() },
    { value: "delivered", label: m.status_delivered() },
    { value: "rejected", label: m.status_rejected() },
    { value: "cancelled", label: m.status_cancelled() },
  ];
}

function normalizeSampleStatus(status?: string) {
  const normalized = String(status ?? "").trim().toLowerCase();
  if (normalized === "requested") return "pending";
  if (normalized === "canceled") return "cancelled";
  return normalized || "pending";
}

export function SampleEditDialog({ open, onOpenChange, sample }: SampleEditDialogProps) {
  useAppLocale();
  const [form, setForm] = React.useState(toForm(sample));
  const [activeAction, setActiveAction] = React.useState<null | "save" | "approve" | "reject" | "ship">(null);
  const updateSample = useMutation(api.samples.updateSampleRequest);
  const approveMutation = useMutation(api.samples.approveSampleRequest);
  const rejectMutation = useMutation(api.samples.rejectSampleRequest);
  const markShippedMutation = useMutation(api.samples.markSampleAsShipped);
  const carriers = useCarriers();

  React.useEffect(() => {
    if (sample && open) setForm(toForm(sample));
  }, [sample, open]);

  const set = <K extends keyof ReturnType<typeof toForm>>(key: K, value: ReturnType<typeof toForm>[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const sampleId = sample?._id ?? sample?.id;
  const currentStatus = normalizeSampleStatus(sample?.status);
  const isSubmitting = activeAction !== null;

  const handleSave = async () => {
    if (isSubmitting) return;
    const notes = form.notes.trim();
    if (form.status === "rejected" && !notes) {
      toast.error(m.samples_edit_provide_rejection_reason());
      return;
    }
    setActiveAction("save");
    try {
      await updateSample({
        sampleRequestId: sampleId as any,
        status: form.status || undefined,
        carrierId: form.carrierId || undefined,
        trackingNumber: form.trackingNumber || undefined,
        labelUrl: form.labelUrl || undefined,
        notes: form.notes || undefined,
        rejectionReason: form.status === "rejected" ? notes : undefined,
      });
      toast.success(m.samples_edit_sample_updated());
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.samples_edit_update_failed());
    } finally {
      setActiveAction(null);
    }
  };

  const handleApprove = async () => {
    if (isSubmitting) return;
    setActiveAction("approve");
    try {
      await approveMutation({ sampleRequestId: sampleId as any, notes: form.notes || undefined });
      toast.success(m.samples_edit_sample_approved());
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.samples_edit_approve_failed());
    } finally {
      setActiveAction(null);
    }
  };

  const handleReject = async () => {
    if (isSubmitting) return;
    if (!form.notes?.trim()) {
      toast.error(m.samples_edit_provide_rejection_reason());
      return;
    }
    setActiveAction("reject");
    try {
      await rejectMutation({ sampleRequestId: sampleId as any, rejectionReason: form.notes.trim() });
      toast.success(m.samples_edit_sample_rejected());
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.samples_edit_reject_failed());
    } finally {
      setActiveAction(null);
    }
  };

  const handleMarkShipped = async () => {
    if (isSubmitting) return;
    setActiveAction("ship");
    try {
      await markShippedMutation({
        sampleRequestId: sampleId as any,
        trackingNumber: form.trackingNumber || undefined,
        carrierId: form.carrierId || undefined,
        labelUrl: form.labelUrl || undefined,
      });
      toast.success(m.samples_edit_sample_marked_shipped());
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.samples_edit_mark_shipped_failed());
    } finally {
      setActiveAction(null);
    }
  };

  const customerName = sample?.user?.company || sample?.user?.fullName || sample?.contactEmail || "Unknown";
  const customerEmail = sample?.contactEmail || sample?.user?.email || "";
  const customerPhone = sample?.contactPhone || "";
  const addr = sample?.shippingAddress;
  const addressStr = addr
    ? [addr.street, addr.city, addr.state, addr.postalCode, addr.country].filter(Boolean).join(", ")
    : "";
  const productName = sample?.product?.name || sample?.cargo?.product?.name || "";
  const cargoOci = sample?.cargo?.ociNumber || "";

  const statusOptions = getStatusOptions();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{m.samples_edit_dialog_title()}</DialogTitle>
          <DialogDescription>
            {customerName} — {formatStatusLabel(sample?.status)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Read-only info */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">{m.samples_customer()}</p>
              <p className="font-medium">{customerName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{m.samples_email()}</p>
              <p>{customerEmail || "-"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{m.samples_phone()}</p>
              <p>{customerPhone || "-"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{m.type()}</p>
              <p>{sample?.sampleType || "-"}</p>
            </div>
            {productName && (
              <div>
                <p className="text-xs text-muted-foreground">{m.product()}</p>
                <p>{productName}</p>
              </div>
            )}
            {cargoOci && (
              <div>
                <p className="text-xs text-muted-foreground">{m.cargo()}</p>
                <p>{cargoOci}</p>
              </div>
            )}
            {addressStr && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">{m.samples_edit_delivery_address()}</p>
                <p>{addressStr}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground">{m.samples_requested()}</p>
              <p>{formatDate(sample?.createdAt || sample?._creationTime)}</p>
            </div>
          </div>

          {/* Quick actions */}
          {(currentStatus === "pending" || currentStatus === "approved") && (
            <>
              <Separator />
              <div className="flex flex-wrap gap-2">
                {currentStatus === "pending" && (
                  <>
                    <Button size="sm" disabled={isSubmitting} onClick={() => void handleApprove()}>
                      {activeAction === "approve" ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          {m.samples_approving()}
                        </>
                      ) : (
                        m.samples_approve()
                      )}
                    </Button>
                    <Button size="sm" variant="destructive" disabled={isSubmitting} onClick={() => void handleReject()}>
                      {activeAction === "reject" ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          {m.samples_rejecting()}
                        </>
                      ) : (
                        m.samples_reject()
                      )}
                    </Button>
                  </>
                )}
                {currentStatus === "approved" && (
                  <Button size="sm" disabled={isSubmitting} onClick={() => void handleMarkShipped()}>
                    {activeAction === "ship" ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        {m.samples_edit_marking()}
                      </>
                    ) : (
                      m.samples_mark_shipped()
                    )}
                  </Button>
                )}
              </div>
            </>
          )}

          <Separator />

          {/* Shipping form */}
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>{m.status()}</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>{m.samples_carrier()}</Label>
              <Select value={form.carrierId} onValueChange={(v) => set("carrierId", v)}>
                <SelectTrigger><SelectValue placeholder={m.samples_edit_select_carrier_placeholder()} /></SelectTrigger>
                <SelectContent>
                  {carriers?.map((c: any) => (
                    <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>{m.samples_tracking_number()}</Label>
              <Input value={form.trackingNumber} onChange={(e) => set("trackingNumber", e.target.value)} placeholder={m.samples_edit_tracking_number_placeholder()} />
            </div>

            <div className="grid gap-2">
              <Label>{m.samples_edit_label_url()}</Label>
              <div className="flex gap-2">
                <Input value={form.labelUrl} onChange={(e) => set("labelUrl", e.target.value)} placeholder="https://..." className="flex-1" />
                {form.labelUrl && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={form.labelUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-2">
              <Label>{m.notes()}</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={m.samples_edit_notes_placeholder()} rows={3} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>{m.cancel()}</Button>
          <Button onClick={() => void handleSave()} disabled={isSubmitting}>
            {activeAction === "save" ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {m.saving()}
              </>
            ) : (
              m.save_changes()
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toForm(s: any) {
  return {
    status: normalizeSampleStatus(s?.status),
    carrierId: s?.carrierId ?? "",
    trackingNumber: s?.trackingNumber ?? "",
    labelUrl: s?.labelUrl ?? "",
    notes: s?.notes ?? s?.rejectionReason ?? "",
  };
}
